/**
 * bozza — lo stato di modifica del costruttore di questionari e le conversioni da e verso il
 * documento salvato. PURA (niente React, niente Firestore).
 *
 * La bozza è il questionario normalizzato (`shared/questionari`) più due cose che servono solo
 * mentre si scrive: una chiave stabile per React e il segno «codice scritto a mano» (finché non
 * lo è, il codice di una domanda o di un'opzione segue il testo con `idDaEtichetta`).
 *
 * I codici BLOCCATI: se il questionario ha già risposte, i codici di domande e opzioni esistenti
 * non si cambiano più (le statistiche e le risposte salvate contano i codici); si cambia il testo.
 */

import {
  idDaEtichetta,
  normalizzaQuestionario,
  TIPI_CON_OPZIONI,
  type CampoContatto,
  type CampoDettaglio,
  type Domanda,
  type DomandaSalvata,
  type EmailConferma,
  type OpzioneDomanda,
  type QuestionarioSalvato,
  type SchermataApertura,
  type SchermataConferma,
  type SchermataContatti,
  type ScorciatoiaNumero,
  type TipoDomanda,
  type TraduzioneQuestionario,
} from '@shared/questionari';
import type { PlatformQuestionnaire } from '../../../types/platformCrm';

export interface BozzaOpzione extends OpzioneDomanda {
  chiave: string;
  /** Vero se il codice l'ha scritto la persona (o se viene dal documento salvato). */
  idFisso: boolean;
}

export interface BozzaDomanda extends Omit<Domanda, 'opzioni'> {
  chiave: string;
  idFisso: boolean;
  opzioni: BozzaOpzione[];
}

export interface Bozza {
  titolo: string;
  descrizione: string;
  attivo: boolean;
  lingua_sorgente: string;
  domande: BozzaDomanda[];
  apertura: SchermataApertura | null;
  contatti: SchermataContatti | null;
  conferma: SchermataConferma | null;
  email_conferma: EmailConferma | null;
  traduzioni: Record<string, TraduzioneQuestionario>;
}

/** I codici che non si cambiano più (questionario con risposte). */
export interface CodiciBloccati {
  domande: Set<string>;
  /** Per codice di domanda, i codici delle sue opzioni. */
  opzioni: Map<string, Set<string>>;
}

export const NESSUN_CODICE_BLOCCATO: CodiciBloccati = { domande: new Set(), opzioni: new Map() };

function nuovaChiave(): string {
  return crypto.randomUUID();
}

/** Il codice proposto per un testo, reso unico rispetto a quelli già usati. */
export function codiceUnico(base: string, usati: string[], massimo = 40): string {
  const radice = idDaEtichetta(base).slice(0, massimo) || 'voce';
  if (!usati.includes(radice)) return radice;
  for (let i = 2; i < 1000; i++) {
    const candidato = `${radice.slice(0, massimo - String(i).length - 1)}_${i}`;
    if (!usati.includes(candidato)) return candidato;
  }
  return `${radice}_${nuovaChiave().slice(0, 6)}`;
}

export function nuovaOpzione(etichetta = '', usati: string[] = []): BozzaOpzione {
  return {
    chiave: nuovaChiave(),
    id: etichetta ? codiceUnico(etichetta, usati) : '',
    etichetta,
    idFisso: false,
  };
}

export function nuovaDomanda(usati: string[]): BozzaDomanda {
  return {
    chiave: nuovaChiave(),
    id: codiceUnico('domanda', usati),
    idFisso: false,
    tipo: 'scelta_singola',
    testo: '',
    obbligatoria: true,
    opzioni: [],
  };
}

function bozzaDomanda(d: Domanda): BozzaDomanda {
  return {
    ...d,
    chiave: nuovaChiave(),
    idFisso: true,
    opzioni: (d.opzioni ?? []).map((o) => ({ ...o, chiave: nuovaChiave(), idFisso: true })),
  };
}

/**
 * La bozza da un documento (o vuota). I questionari di prima (opzioni come stringhe,
 * `richiede_anagrafica`) passano da `normalizzaQuestionario`: le opzioni diventano oggetti con
 * codice = testo (le risposte salvate restano valide) e l'anagrafica diventa un'ultima schermata
 * esplicita con gli stessi campi e obblighi.
 */
export function bozzaDaQuestionario(q: PlatformQuestionnaire | null): Bozza {
  if (!q) {
    return {
      titolo: '',
      descrizione: '',
      attivo: true,
      lingua_sorgente: 'it',
      domande: [nuovaDomanda([])],
      apertura: null,
      contatti: null,
      conferma: null,
      email_conferma: null,
      traduzioni: {},
    };
  }
  const n = normalizzaQuestionario(q);
  const traduzioni: Record<string, TraduzioneQuestionario> = {};
  Object.entries(q.traduzioni ?? {}).forEach(([lingua, voce]) => {
    if (voce && typeof voce === 'object') traduzioni[lingua] = JSON.parse(JSON.stringify(voce)) as TraduzioneQuestionario;
  });
  return {
    titolo: n.titolo ?? '',
    descrizione: n.descrizione ?? '',
    attivo: n.attivo !== false,
    lingua_sorgente: n.lingua_sorgente || 'it',
    domande: n.domande.map(bozzaDomanda),
    apertura: n.apertura ?? null,
    contatti: n.contatti ?? null,
    conferma: n.conferma ?? null,
    email_conferma: n.email_conferma ?? null,
    traduzioni,
  };
}

/** I codici che il documento ha già, da bloccare se ci sono risposte. */
export function codiciDelQuestionario(q: PlatformQuestionnaire | null): CodiciBloccati {
  if (!q) return NESSUN_CODICE_BLOCCATO;
  const n = normalizzaQuestionario(q);
  const opzioni = new Map<string, Set<string>>();
  n.domande.forEach((d) => opzioni.set(d.id, new Set((d.opzioni ?? []).map((o) => o.id))));
  return { domande: new Set(n.domande.map((d) => d.id)), opzioni };
}

// ─────────────────────────────────────────────────────────────────────────────
// Dalla bozza al documento
// ─────────────────────────────────────────────────────────────────────────────

/** Una stringa vuota non si salva: il campo facoltativo resta assente. */
function testo(v: string | undefined | null): string | undefined {
  return typeof v === 'string' && v.trim() ? v.trim() : undefined;
}

function domandaDaBozza(b: BozzaDomanda, idDomande: string[]): DomandaSalvata {
  const d: DomandaSalvata = {
    id: b.id.trim(),
    tipo: b.tipo,
    testo: b.testo.trim(),
    obbligatoria: b.obbligatoria,
  };
  const nomeBreve = testo(b.nome_breve);
  if (nomeBreve) d.nome_breve = nomeBreve;
  const aiuto = testo(b.aiuto);
  if (aiuto) d.aiuto = aiuto;

  const conOpzioni = TIPI_CON_OPZIONI.includes(b.tipo);
  if (conOpzioni) {
    d.opzioni = b.opzioni.map((o) => {
      const opzione: OpzioneDomanda = { id: o.id.trim(), etichetta: o.etichetta.trim() };
      if (o.esclusiva && b.tipo === 'scelta_multipla') opzione.esclusiva = true;
      return opzione;
    });
    if (b.tipo === 'scelta_multipla' && typeof b.massimo_scelte === 'number') d.massimo_scelte = b.massimo_scelte;
    if (b.campo_dettaglio) {
      const cd: CampoDettaglio = {
        etichetta: b.campo_dettaglio.etichetta.trim(),
        mostra_se: b.campo_dettaglio.mostra_se.filter((id) => b.opzioni.some((o) => o.id === id)),
      };
      const segnaposto = testo(b.campo_dettaglio.segnaposto);
      if (segnaposto) cd.segnaposto = segnaposto;
      if (b.campo_dettaglio.obbligatorio) cd.obbligatorio = true;
      d.campo_dettaglio = cd;
    }
  } else {
    const segnaposto = testo(b.segnaposto);
    if (segnaposto && (b.tipo === 'testo_breve' || b.tipo === 'testo_lungo' || b.tipo === 'numero')) d.segnaposto = segnaposto;
  }

  if (b.tipo === 'numero') {
    if (typeof b.minimo === 'number') d.minimo = b.minimo;
    if (typeof b.massimo === 'number') d.massimo = b.massimo;
    const scorciatoie: ScorciatoiaNumero[] = (b.scorciatoie ?? [])
      .filter((s) => Number.isFinite(s.valore))
      .map((s) => (testo(s.etichetta) ? { valore: s.valore, etichetta: testo(s.etichetta) } : { valore: s.valore }));
    if (scorciatoie.length > 0) d.scorciatoie = scorciatoie;
  }

  // Il salto vale solo per le risposte chiuse a valore singolo, verso una domanda che esiste.
  if (
    b.salta_a &&
    (b.tipo === 'scelta_singola' || b.tipo === 'si_no') &&
    b.salta_a.se_risposta &&
    idDomande.includes(b.salta_a.vai_a_domanda_id)
  ) {
    d.salta_a = { se_risposta: b.salta_a.se_risposta, vai_a_domanda_id: b.salta_a.vai_a_domanda_id };
  }
  return d;
}

function aperturaDaBozza(a: SchermataApertura): SchermataApertura {
  const out: SchermataApertura = { titolo: a.titolo.trim() };
  if (testo(a.testo)) out.testo = testo(a.testo);
  if (testo(a.etichetta)) out.etichetta = testo(a.etichetta);
  if (testo(a.bottone)) out.bottone = testo(a.bottone);
  return out;
}

function contattiDaBozza(c: SchermataContatti): SchermataContatti {
  const out: SchermataContatti = {
    domanda: c.domanda.trim(),
    campi: c.campi.map((campo) => {
      const pulito: CampoContatto = { id: campo.id, etichetta: campo.etichetta.trim(), obbligatorio: campo.obbligatorio };
      if (testo(campo.segnaposto)) pulito.segnaposto = testo(campo.segnaposto);
      if (testo(campo.nota)) pulito.nota = testo(campo.nota);
      return pulito;
    }),
  };
  if (testo(c.indicatore)) out.indicatore = testo(c.indicatore);
  if (testo(c.aiuto)) out.aiuto = testo(c.aiuto);
  if (testo(c.bottone)) out.bottone = testo(c.bottone);
  if (c.consenso) {
    out.consenso = {
      testo: c.consenso.testo.trim(),
      versione: c.consenso.versione.trim(),
      canali: c.consenso.canali,
    };
    if (testo(c.consenso.etichetta_informativa)) out.consenso.etichetta_informativa = testo(c.consenso.etichetta_informativa);
    if (testo(c.consenso.url_informativa)) out.consenso.url_informativa = testo(c.consenso.url_informativa);
  }
  return out;
}

function confermaDaBozza(c: SchermataConferma): SchermataConferma {
  const out: SchermataConferma = { titolo: c.titolo.trim() };
  if (testo(c.in_grande)) out.in_grande = testo(c.in_grande);
  if (testo(c.testo)) out.testo = testo(c.testo);
  if (testo(c.testo_se_consenso)) out.testo_se_consenso = testo(c.testo_se_consenso);
  if (c.collegamento && (testo(c.collegamento.url) || testo(c.collegamento.etichetta))) {
    out.collegamento = {
      prima: c.collegamento.prima.trim(),
      etichetta: c.collegamento.etichetta.trim(),
      url: c.collegamento.url.trim(),
    };
  }
  return out;
}

function emailDaBozza(e: EmailConferma): EmailConferma {
  const out: EmailConferma = {
    attiva: e.attiva,
    oggetto: e.oggetto.trim(),
    titolo: e.titolo.trim(),
    testo: e.testo.trim(),
    perche: e.perche.trim(),
  };
  (['anteprima', 'in_grande', 'titolo_riepilogo', 'testo_consenso', 'bottone_consenso', 'etichetta_uscita'] as const).forEach((k) => {
    const v = testo(e[k]);
    if (v) out[k] = v;
  });
  return out;
}

/**
 * Le traduzioni senza voci vuote: una stringa vuota vale come assente (regola dei testi
 * multilingua), e un oggetto rimasto vuoto si toglie. Le lingue senza nessun testo spariscono.
 */
export function traduzioniPulite(t: Record<string, TraduzioneQuestionario>): Record<string, TraduzioneQuestionario> {
  const pota = (v: unknown): unknown => {
    if (typeof v === 'string') return v.trim() ? v : undefined;
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      const out: Record<string, unknown> = {};
      Object.entries(v as Record<string, unknown>).forEach(([k, x]) => {
        const p = pota(x);
        if (p !== undefined) out[k] = p;
      });
      return Object.keys(out).length > 0 ? out : undefined;
    }
    return undefined;
  };
  const out: Record<string, TraduzioneQuestionario> = {};
  Object.entries(t).forEach(([lingua, voce]) => {
    const p = pota(voce);
    if (p) out[lingua] = p as TraduzioneQuestionario;
  });
  return out;
}

/** Il documento da salvare (i campi di audit li aggiunge il servizio). */
export function questionarioDaBozza(b: Bozza): QuestionarioSalvato {
  const idDomande = b.domande.map((d) => d.id.trim());
  return {
    titolo: b.titolo.trim(),
    descrizione: testo(b.descrizione) ?? null,
    attivo: b.attivo,
    lingua_sorgente: b.lingua_sorgente,
    // `richiede_anagrafica` è il modo vecchio di dire «c'è l'ultima schermata»: lo si tiene
    // allineato, così un questionario senza contatti non ne fa ricomparire di storici.
    richiede_anagrafica: b.contatti !== null,
    domande: b.domande.map((d) => domandaDaBozza(d, idDomande)),
    apertura: b.apertura ? aperturaDaBozza(b.apertura) : null,
    contatti: b.contatti ? contattiDaBozza(b.contatti) : null,
    conferma: b.conferma ? confermaDaBozza(b.conferma) : null,
    email_conferma: b.email_conferma ? emailDaBozza(b.email_conferma) : null,
    traduzioni: traduzioniPulite(b.traduzioni),
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Valori proposti quando si accende una schermata
// ─────────────────────────────────────────────────────────────────────────────

export function aperturaVuota(titolo: string): SchermataApertura {
  return { titolo, testo: '', etichetta: '', bottone: '' };
}

export function contattiVuoti(): SchermataContatti {
  return {
    domanda: '',
    campi: [{ id: 'email', etichetta: 'Email', obbligatorio: true }],
    bottone: '',
  };
}

export function confermaVuota(): SchermataConferma {
  return { titolo: '', testo: '' };
}

export function emailVuota(): EmailConferma {
  return { attiva: true, oggetto: '', titolo: '', testo: '', perche: '' };
}

/** Il tipo cambia: si tolgono i campi che col nuovo tipo non hanno senso. */
export function cambiaTipo(d: BozzaDomanda, tipo: TipoDomanda): BozzaDomanda {
  const conOpzioni = TIPI_CON_OPZIONI.includes(tipo);
  const out: BozzaDomanda = { ...d, tipo, opzioni: conOpzioni ? d.opzioni : [] };
  if (!conOpzioni) delete out.campo_dettaglio;
  if (tipo !== 'scelta_multipla') {
    delete out.massimo_scelte;
    out.opzioni = out.opzioni.map((o) => ({ ...o, esclusiva: undefined }));
  }
  if (tipo !== 'numero') {
    delete out.minimo;
    delete out.massimo;
    delete out.scorciatoie;
  }
  if (tipo !== 'scelta_singola' && tipo !== 'si_no') delete out.salta_a;
  return out;
}

// ─────────────────────────────────────────────────────────────────────────────
// Un codice cambia: i riferimenti lo seguono
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Una domanda è cambiata (stessa chiave React): se il suo codice o quelli delle sue opzioni sono
 * cambiati, le traduzioni (indicizzate per codice) e i salti delle altre domande li seguono.
 * Senza questo, una traduzione resterebbe attaccata a un codice che non esiste più.
 */
export function applicaDomandaCambiata(b: Bozza, vecchia: BozzaDomanda, nuova: BozzaDomanda): Bozza {
  let traduzioni = b.traduzioni;
  const domande = b.domande.map((d) => (d.chiave === nuova.chiave ? nuova : d));

  const opzioniRinominate: Array<[string, string]> = [];
  vecchia.opzioni.forEach((o) => {
    const n = nuova.opzioni.find((x) => x.chiave === o.chiave);
    if (n && n.id !== o.id && o.id) opzioniRinominate.push([o.id, n.id]);
  });

  if (vecchia.id !== nuova.id || opzioniRinominate.length > 0) {
    traduzioni = {};
    Object.entries(b.traduzioni).forEach(([lingua, voce]) => {
      const copia: TraduzioneQuestionario = { ...voce, domande: { ...(voce.domande ?? {}) } };
      const td = copia.domande![vecchia.id];
      if (td) {
        const nuovaTd = { ...td, opzioni: td.opzioni ? { ...td.opzioni } : undefined };
        opzioniRinominate.forEach(([da, a]) => {
          if (nuovaTd.opzioni && nuovaTd.opzioni[da] !== undefined) {
            nuovaTd.opzioni[a] = nuovaTd.opzioni[da];
            delete nuovaTd.opzioni[da];
          }
        });
        delete copia.domande![vecchia.id];
        copia.domande![nuova.id] = nuovaTd;
      }
      traduzioni[lingua] = copia;
    });
  }

  if (vecchia.id === nuova.id) return { ...b, domande, traduzioni };
  return {
    ...b,
    traduzioni,
    domande: domande.map((d) =>
      d.salta_a && d.salta_a.vai_a_domanda_id === vecchia.id
        ? { ...d, salta_a: { ...d.salta_a, vai_a_domanda_id: nuova.id } }
        : d,
    ),
  };
}
